import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import DocumentDraftModal from '../components/drafts/DocumentDraftModal';
import { REPORT_TEMPLATES, REPORT_CATEGORIES } from '../data/reportTemplates';
import { useApp } from '../context/AppContext';
import Icon from '../components/common/Icon';
import PageHeader from '../components/common/PageHeader';
import Chip from '../components/common/Chip';
import { FiZap, FiEye, FiSearch, FiX } from 'react-icons/fi';

const CAT_STYLES = {
  board: { bg: '#e4f5f3', color: '#145f5f', border: 'rgba(20, 95, 95, 0.15)' },
  audit: { bg: '#edeef3', color: '#3a4a66', border: 'rgba(58, 74, 102, 0.15)' },
  other: { bg: '#fbf5e6', color: '#8a6a1f', border: 'rgba(138, 106, 31, 0.15)' },
};

const BADGE_TONE = {
  green: { bg: '#e8efe5', color: '#4a6b3f' },
  orange: { bg: '#f3ebd7', color: '#8a6a1f' },
  blue: { bg: '#edeef3', color: '#3a4a66' },
  gold: { bg: '#f3ebd7', color: '#836016' },
};

function ReportCard({ template, onDraft, onPreview }) {
  const catStyle = CAT_STYLES[template.category] || CAT_STYLES.board;
  const badge = template.badge ? (BADGE_TONE[template.badge.color] || BADGE_TONE.gold) : null;

  return (
    <div
      className="cs-resolution-card group rounded-[14px] px-4 py-3.5 cursor-pointer flex flex-col justify-between"
      onClick={() => onDraft(template)}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span
            className="shrink-0 grid place-items-center w-8 h-8 rounded-[9px] border transition-all duration-300 group-hover:scale-105"
            style={{ background: catStyle.bg, color: catStyle.color, borderColor: catStyle.border }}
          >
            <Icon name={template.icon} size={15} />
          </span>
          {badge && (
            <Chip bg={badge.bg} color={badge.color} className="uppercase tracking-[0.5px] !text-[9px] !px-2 !font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {template.badge.text}
            </Chip>
          )}
        </div>
        
        <div>
          <div
            className="text-[13px] font-bold leading-[1.3] text-[#08111f] mb-0.5 transition-colors group-hover:text-[#8a6a1f]"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.01em' }}
          >
            {template.title}
          </div>
          <div
            className="text-[11px] text-[#7a7060] leading-relaxed mb-2.5"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {template.description}
          </div>
        </div>
      </div>

      <div>
        <div className="flex gap-1.5 flex-wrap mb-2.5">
          {template.tags.map((tag) => (
            <Chip key={tag} bg="#fdfaf4" color="#8a7d66" className="!text-[10px] !font-bold" style={{ borderColor: 'rgba(226,217,200,0.65)', fontFamily: "'Outfit', sans-serif" }}>
              {tag}
            </Chip>
          ))}
        </div>

        <div
          className="flex items-center gap-2 pt-2 border-t border-[#f2eee6]"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            title="Draft with AI"
            onClick={() => onDraft(template)}
            className="flex items-center justify-center w-7 h-7 rounded-[8px] border border-[#ddd6c6] bg-white text-[#8a6a1f] hover:bg-[#faf7f2] hover:border-[#cdc4b0] transition-all active:scale-95"
          >
            <FiZap size={13} />
          </button>
          {template.hasPreview && (
            <button
              type="button"
              title="Preview Template"
              onClick={() => onPreview(template)}
              className="flex items-center justify-center w-7 h-7 rounded-[8px] border border-[#ddd6c6] bg-white text-[#5a5145] hover:bg-[#faf7f2] hover:border-[#cdc4b0] transition-all active:scale-95"
            >
              <FiEye size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReportTemplatesPage() {
  const location = useLocation();
  const { activeCompany } = useApp();

  const [activeCategory, setActiveCategory] = useState('all');
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  /* Deep-link from spotlight search */
  useEffect(() => {
    const reportId = location.state?.reportId;
    if (!reportId) return;
    const found = REPORT_TEMPLATES.find((rt) => rt.id === reportId);
    if (found) setSelected({ doc: found });
    window.history.replaceState({}, '');
  }, [location.state?.reportId]);

  /* Filter by category and search query */
  const filtered = useMemo(() => {
    return REPORT_TEMPLATES.filter((rt) => {
      const matchesCategory = activeCategory === 'all' || rt.category === activeCategory;
      const matchesSearch = 
        rt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rt.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rt.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const hasResults = filtered.length > 0;

  return (
    <AppLayout pageTitle="Report Templates">
      <PageHeader
        title={(
          <span className="inline-flex items-center gap-2 align-middle">
            <Icon name="analytics" size={22} style={{ color: '#145f5f' }} />
            Report Templates
          </span>
        )}
        subtitle="Board Report & Auditor's Report — updated as per Companies Act 2013 (latest amendments, FY 2024-25)"
      />

      {/* Search Input Bar + AI status chip */}
      <div className="flex items-center gap-3 mb-4 flex-wrap sm:flex-nowrap">
        <div className="relative flex-1 min-w-0">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7a7060]" size={16} />
          <input
            type="text"
            placeholder="Search report templates by name, description, or regulation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-[38px] pr-9 py-2.5 rounded-xl border-[1.5px] text-[13px] outline-none transition-all focus:border-[#145f5f] focus:shadow-sm"
            style={{ borderColor: '#ddd6c6', background: '#ffffff', fontFamily: "'Outfit', sans-serif" }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#7a7060] hover:text-[#08111f]"
            >
              <FiX size={15} />
            </button>
          )}
        </div>
        <Chip bg="#fbf5e6" color="#8a6a1f" className="inline-flex items-center gap-1.5 shrink-0 !py-2.5 !px-3.5 rounded-xl border border-[#ecdca8] !text-[11.5px] !font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <Icon name="✨" size={11} /> AI Drafting Available
        </Chip>
      </div>

      {/* Category Tabs Pill Selector */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 cs-premium-scrollbar">
        {REPORT_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-3.5 py-1.5 rounded-xl text-[12px] font-bold transition-all whitespace-nowrap border ${
              activeCategory === cat.id
                ? 'bg-[#145f5f] border-[#145f5f] text-white shadow-sm'
                : 'bg-white border-[#e2d9c8] text-[#5a5145] hover:bg-[#faf9f6]'
            }`}
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Report Cards Grid */}
      {hasResults ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {filtered.map((template) => (
            <ReportCard
              key={template.id}
              template={template}
              onDraft={(t) => setSelected({ doc: t })}
              onPreview={(t) => setSelected({ doc: t })}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-2xl border" style={{ borderColor: '#e2d9c8' }}>
          <div className="text-[28px] mb-2">🔍</div>
          <h3 className="text-[14px] font-bold text-[#08111f]">No reports found</h3>
          <p className="text-[12px] text-[#7a7060] mt-1">
            We couldn't find any reports matching "{searchQuery}". Try adjusting your keywords.
          </p>
          <button
            onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
            className="mt-4 px-4 py-2 bg-[#fbf5e6] text-[#8a6a1f] border border-[#8a6a1f]/20 rounded-xl text-[12px] font-semibold hover:bg-[#faf4e2]"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Legal Update Note */}
      <div
        className="mt-6 flex items-start gap-3 px-4 py-3.5 rounded-xl border border-[#ecdca8] bg-[#fdfbf7] text-[12.5px] leading-relaxed text-[#7a6018]"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        <span className="shrink-0 mt-[2px] text-[#b8973a]"><Icon name="📌" size={14} /></span>
        <div>
          <strong>Legal Update Note:</strong> All templates are updated as per Companies Act 2013
          including amendments up to 2024. CARO 2020 reporting requirements are incorporated.
          Board Reports include disclosures required post Companies (Amendment) Act, 2020 &mdash;
          including CSR annual report format, MSME dues disclosure, and energy conservation statements.
          Auditor&apos;s Reports align with ICAI&apos;s SA 700 Revised standards.
        </div>
      </div>

      {/* Modal (reused from Document Library) */}
      <DocumentDraftModal
        selected={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        pageOrigin="report-templates"
        companyCin={activeCompany?.cin}
        companyName={activeCompany?.name}
      />
    </AppLayout>
  );
}
